import toInteger from 'lodash/toInteger';
import RiskMatrix from 'models/RiskMatrix';
import WorkingHoursPattern from 'models/WorkingHoursPattern';
import AppError from 'utils/appError';
import { toBool } from '../utils/string';
import { PlannerBusinessScenario } from './plannerBusinessScenario';
import { SelectedSitesSteps } from './SelectedSitesSteps';

//general settings
export const AutoUserLicense = 'AutoUserLicense';
export const DefLanguageCode = 'DefLanguageCode';
export const AuditYearStart = 'AuditYearStart';
export const RiskScoreDef = 'RiskScoreDef';
export const WorkingHours = 'WorkingHours';
export const OrgLanguageCodes = 'OrgLanguageCodes';
export const ModuleRisks = 'ModuleRisks';
export const ModuleOrg = 'ModuleOrg';
export const ModuleThemes = 'ModuleThemes';
export const ModuleControls = 'ModuleControls';
export const ModuleAssets = 'ModuleAssets';
export const ModuleStandards = 'ModuleStandards';
export const ModuleProcessingActivities = 'ModuleProcessingActivities';
export const LibraryOpenInModal = 'LibraryOpenInModal';
export const ShowISOElementAds = 'ShowISOElementAds';
export const LegacyOutlookSeriesEnabled = 'LegacyOutlookSeriesEnabled';
export const Risk3dMatrix = 'Risk3dMatrix';
export const PreviewFeatureToggles = 'PreviewFeatureToggles';
export const PrivacyFrameworkSetting = 'PrivacyFramework';

//report settings
export const ReportStorageList = 'ReportStorageList';
export const ReportTemplateLink = 'ReportTemplateLink';
export const ReportDefaultStorageBehavior = 'ReportDefaultStorageBehavior';
export const ReportDefaultOutputFormat = 'ReportDefaultOutputFormat';
export const ReportAllowUserSettingOverride = 'ReportAllowUserSettingOverride';
export const ReportKeepWordNextToPDF = 'ReportKeepWordNextToPDF';

//auth settings
export const AzureAD = 'AzureAD';
export const AzureADGroupSync = 'AzureADGroupSync';
export const OutlookTasksAdvanced = 'OutlookTasksAdvanced';
export const OutlookTasksBasic = 'OutlookTasksBasic';
export const SharepointLibraries = 'SharepointLibraries';
export const SharepointPages = 'SharepointPages';
export const SharepointReportsAndPublishing = 'SharepointReports';
export const SharepointSearch = 'SharepointSearch';
export const TeamsKB = 'TeamsKB';
export const TeamsTasks = 'TeamsTasks';
export const PowerBI = 'PowerBI';
export const PowerBIService = 'PowerBIService';
export const TeamsKBTaskTemplate = 'TeamsKBTaskTemplate';
export const TeamsCollaboration = 'TeamsCollaboration';
export const Planner = 'Planner';
export const AzureADPhotos = 'AzureADPhotos';
export const APPSelectedSites = 'APPSelectedSites';
export const SVCSelectedSites = 'SVCSelectedSites';
export const APISelectedSites = 'APISelectedSites';
export const MicrosoftCloudSecureScore = 'MicrosoftCloudSecureScore';
export const Microsoft365SecureScore = 'Microsoft365SecureScore';
export const MicrosoftIdentitySecureScore = 'MicrosoftIdentitySecureScore';
export const MicrosoftDevicesSecureScore = 'MicrosoftDevicesSecureScore';

//automation settings
export const PowerAutomateSharePoint = 'PowerAutomateSharePoint';
export const AIEnabled = 'AIEnabled';

//setting enums
export enum SelectedSitesStep {
  None = 0,
  Auth = 1,
  Config = 2,
  Enabled = 3,
}

export class Settings {
  private static _workingHoursPattern: WorkingHoursPattern | undefined;

  items: Setting[];

  constructor() {
    this.items = [];
  }

  //Privately cached setting for fast access
  getStaticWorkingHours(): WorkingHoursPattern {
    if (Settings._workingHoursPattern) return Settings._workingHoursPattern;
    Settings._workingHoursPattern = this.get(WorkingHours);

    return Settings._workingHoursPattern ?? new WorkingHoursPattern();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(name: string): any {
    const setting = this.items.find((s) => {
      return s.settingName === name;
    });
    if (setting) {
      return setting.getValue();
    } else {
      return undefined;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set(name: string, value: any) {
    const setting = this.items.find((s) => {
      return s.settingName === name;
    });
    if (setting) {
      setting.setValue(setting.settingName, value);
    } else {
      return undefined;
    }
  }

  clone(): Settings {
    const newSettings = new Settings();
    newSettings.items = this.items.map((s) => s.clone());

    return newSettings;
  }
}

export default class Setting {
  settingName: string;

  settingValue: string | undefined;

  constructor() {
    this.settingName = '';
    this.settingValue = undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue(): any {
    try {
      const boolSettings = [
        AutoUserLicense,
        SharepointLibraries,
        SharepointPages,
        SharepointReportsAndPublishing,
        SharepointSearch,
        TeamsKB,
        TeamsTasks,
        TeamsCollaboration,
        ModuleRisks,
        ModuleOrg,
        ModuleThemes,
        ModuleControls,
        ModuleAssets,
        ModuleStandards,
        ModuleProcessingActivities,
        OutlookTasksAdvanced,
        OutlookTasksBasic,
        AzureAD,
        MicrosoftCloudSecureScore,
        Microsoft365SecureScore,
        MicrosoftIdentitySecureScore,
        MicrosoftDevicesSecureScore,
        AzureADGroupSync,
        PowerAutomateSharePoint,
        LibraryOpenInModal,
        ReportAllowUserSettingOverride,
        ReportKeepWordNextToPDF,
        ShowISOElementAds,
        PowerBI,
        PowerBIService,
        LegacyOutlookSeriesEnabled,
        AIEnabled,
        AzureADPhotos,
        Risk3dMatrix,
      ];

      const intSettings = [
        ReportStorageList,
        ReportTemplateLink,
        ReportDefaultStorageBehavior,
        ReportDefaultOutputFormat,
        PrivacyFrameworkSetting
      ];

      if (boolSettings.includes(this.settingName)) {
        return toBool(this.settingValue);
      }

      if (intSettings.includes(this.settingName)) {
        return toInteger(this.settingValue);
      }

      switch (this.settingName) {
        case TeamsKBTaskTemplate:
          return Number.parseInt(this.settingValue ?? '');
        case DefLanguageCode:
        case AuditYearStart:
          return this.settingValue;
        case RiskScoreDef:
          const matrix = new RiskMatrix();
          matrix.fromJSON(this.settingValue);

          return matrix;
        case WorkingHours:
          const workingHours: WorkingHoursPattern = new WorkingHoursPattern();
          workingHours.fromJSON(this.settingValue);

          return workingHours;
        case OrgLanguageCodes:
        case PreviewFeatureToggles:
          if (!this.settingValue) return null;

          return JSON.parse(this.settingValue);
        case Planner:
          const plannerBusinessScenario = new PlannerBusinessScenario();
          plannerBusinessScenario.fromJSON(this.settingValue);

          return plannerBusinessScenario;
        case SVCSelectedSites:
        case APPSelectedSites:
        case APISelectedSites:
          return new SelectedSitesSteps(this.settingValue);
        default:
          throw new AppError('Setting not found: ' + this.settingName);
      }
    } catch (err) {
      throw err;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue(setting: string, value: any) {
    try {
      const jsonSettings = [
        RiskScoreDef,
        WorkingHours,
        OrgLanguageCodes,
        PreviewFeatureToggles,
        Planner,
        SVCSelectedSites,
        APPSelectedSites,
        APISelectedSites,
      ];

      const stringSettings = [
        AutoUserLicense,
        DefLanguageCode,
        AuditYearStart,
        SharepointLibraries,
        SharepointPages,
        SharepointReportsAndPublishing,
        SharepointSearch,
        TeamsKB,
        TeamsTasks,
        TeamsCollaboration,
        TeamsKBTaskTemplate,
        ModuleRisks,
        ModuleOrg,
        ModuleThemes,
        ModuleControls,
        ModuleAssets,
        ModuleStandards,
        ModuleProcessingActivities,
        OutlookTasksAdvanced,
        OutlookTasksBasic,
        AzureAD,
        MicrosoftCloudSecureScore,
        Microsoft365SecureScore,
        MicrosoftIdentitySecureScore,
        MicrosoftDevicesSecureScore,
        AzureADGroupSync,
        PowerAutomateSharePoint,
        LibraryOpenInModal,
        ReportStorageList,
        ReportTemplateLink,
        ReportDefaultStorageBehavior,
        ReportDefaultOutputFormat,
        ReportAllowUserSettingOverride,
        ReportKeepWordNextToPDF,
        ShowISOElementAds,
        PowerBI,
        PowerBIService,
        LegacyOutlookSeriesEnabled,
        AIEnabled,
        Planner,
        AzureADPhotos,
        Risk3dMatrix,
        PrivacyFrameworkSetting,
      ];

      this.settingName = setting;

      if (jsonSettings.includes(setting)) {
        switch (this.settingName) {
          case APISelectedSites:
          case SVCSelectedSites:
          case APPSelectedSites:
            this.settingValue = (value as SelectedSitesSteps).toJSON();
            break;
          case RiskScoreDef:
            this.settingValue = (value as RiskMatrix).toJSON();
            break;
          case WorkingHours:
            this.settingValue = (value as WorkingHoursPattern).toJSON();
            break;
          case OrgLanguageCodes:
          case PreviewFeatureToggles:
            this.settingValue = JSON.stringify(value);
            break;
          case Planner:
            this.settingValue = (value as PlannerBusinessScenario).toJSON();
            break;
          default:
            break;
        }
      } else if (stringSettings.includes(setting)) {
        this.settingValue = value.toString();
      } else {
        throw new AppError('Setting not found: ' + this.settingName);
      }
    } catch (err) {
      throw err;
    }
  }

  clone(): Setting {
    const newSetting = new Setting();
    newSetting.settingName = this.settingName;
    newSetting.settingValue = this.settingValue;

    return newSetting;
  }
}
